<div class="one-column-container">
  <!-- Interval is intentionally NOT in the UI anymore: it overlapped with
       Hours of Day (Interval=2 + Hours=[9..17] silently took the intersection)
       and confused the schedule literal. The backend column stays at default 1
       for new schedules; old schedules with non-1 intervals continue to fire
       per their saved value until next edit. To express "every N hours",
       use the Hours of Day "Every Nh from M" presets below. -->

  <mat-slide-toggle [formControl]="dependency" color="primary">
    Wait for previous execution to complete?
  </mat-slide-toggle>

  <!-- ── Weekdays ───────────────────────────────────────────────────────── -->
  <div class="field-section">
    <div class="field-section-header">
      <span class="section-label">
        Weekdays
        <mat-icon class="field-help-icon"
                  matTooltip="Days of the week the schedule may fire. Empty = no day restriction.">help_outline</mat-icon>
      </span>
      <span class="section-actions">
        <a class="preset-link" (click)="setWeekdays(weekdays)">All</a>
        <a class="preset-link" (click)="setWeekdays(['MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY'])">Mon–Fri</a>
        <a class="preset-link" (click)="setWeekdays(['SATURDAY','SUNDAY'])">Weekends</a>
        <a class="preset-link clear-link" (click)="setWeekdays([])">Clear</a>
      </span>
    </div>
    <mat-chip-listbox [multiple]="true" [formControl]="weekdaysControl" class="weekday-row">
      <mat-chip-option *ngFor="let day of weekdays" [value]="day">
        {{day | slice:0:3 | titlecase}}
      </mat-chip-option>
    </mat-chip-listbox>
  </div>

  <!-- ── Hours of the Day — collapsible. Summary shows the picked hours;
       click the row (or the chevron) to expand the 24-cell grid for editing.
       Stays collapsed by default so the form stays scannable. -->
  <div class="field-section">
    <div class="field-section-header">
      <span class="section-label">
        Hours of the Day
        <mat-icon class="field-help-icon"
                  matTooltip="Which hours within the day this schedule may fire. Click the row to expand the picker. Empty = every hour.">help_outline</mat-icon>
      </span>
      <span class="section-actions" *ngIf="hoursExpanded">
        <a class="preset-link" (click)="setHours(hours)">All</a>
        <a class="preset-link" (click)="setBusinessHours()">9–17</a>
        <a class="preset-link" (click)="setOffHours()">Off-hours</a>
        <a class="preset-link"
           [class.active]="customRepeatExpanded"
           (click)="customRepeatExpanded = !customRepeatExpanded"
           matTooltip="Pick any 'every N hours starting at hour H' pattern">custom</a>
        <a class="preset-link clear-link" (click)="setHours([])">Clear</a>
      </span>
    </div>

    <!-- Generic "Every N hours starting at H" mini-form. Replaces the
         hardcoded 2h/3h/4h/6h presets — handles ALL combinations including
         "every 2 hours starting 7 AM" (N=2, H=7 → [7,9,11,13,15,17,19,21,23]). -->
    <div *ngIf="customRepeatExpanded && hoursExpanded" class="custom-repeat-row">
      <span class="crow-label">Every</span>
      <mat-form-field appearance="outline" class="crow-num">
        <input matInput type="number" [(ngModel)]="customEveryN" min="1" max="24" />
      </mat-form-field>
      <span class="crow-label">hours starting at hour</span>
      <mat-form-field appearance="outline" class="crow-num">
        <input matInput type="number" [(ngModel)]="customStartHour" min="0" max="23" />
      </mat-form-field>
      <button mat-flat-button color="primary" type="button"
              (click)="applyCustomRepeat()">Apply</button>
      <span class="crow-preview">→ {{ customRepeatPreview() }}</span>
    </div>

    <button type="button" class="hours-summary" (click)="hoursExpanded = !hoursExpanded">
      <span class="hours-summary-text">{{ hoursSummary() }}</span>
      <mat-icon class="hours-summary-chevron"
                [class.expanded]="hoursExpanded">expand_more</mat-icon>
    </button>

    <!-- Always mounted (toggled via [hidden]) — see component note. *ngIf
         re-created the listbox on every expand, which races Material's
         [multiple] init and silently downgrades to single-select after a
         collapse cycle, dropping the second click. -->
    <mat-chip-listbox [multiple]="true"
                      [formControl]="hoursControl"
                      class="hours-grid compact-grid"
                      [hidden]="!hoursExpanded">
      <mat-chip-option *ngFor="let h of hours" [value]="h">{{ h.trim() }}</mat-chip-option>
    </mat-chip-listbox>
  </div>

  <!-- ── Minute Mode ─────────────────────────────────────────────────────── -->
  <div class="field-section">
    <div class="field-section-header">
      <span class="section-label">
        Minute Mode
        <mat-icon class="field-help-icon"
                  matTooltip="EXECUTE AT: fire on a specific set of minute marks within each chosen hour (e.g. :00, :15, :30). EXECUTE EVERY: fire every N minutes within each chosen hour (e.g. every 10 min).">help_outline</mat-icon>
      </span>
    </div>
    <mat-button-toggle-group [formControl]="executeAtModeControl"
                             hideSingleSelectionIndicator
                             class="minute-mode-toggle">
      <mat-button-toggle value="executeAt">
        <mat-icon class="toggle-icon">schedule</mat-icon>
        Execute At
      </mat-button-toggle>
      <mat-button-toggle value="executeEvery">
        <mat-icon class="toggle-icon">autorenew</mat-icon>
        Execute Every
      </mat-button-toggle>
    </mat-button-toggle-group>
  </div>

  <!-- Minute Marks — same collapsible expand-on-click pattern as Hours of
       the Day above. Summary line shows the picked minutes; click to expand
       the 60-chip grid for fine-grained picking. -->
  <div class="field-section" *ngIf="isHourlyExecuteAt()">
    <div class="field-section-header">
      <span class="section-label">
        Minute Marks
        <mat-icon class="field-help-icon"
                  matTooltip="Specific minutes within each hour the schedule fires. Click the row to expand the picker. Use quick-presets on the right for common patterns.">help_outline</mat-icon>
      </span>
      <span class="section-actions" *ngIf="minutesExpanded">
        <a class="preset-link" (click)="setMinutes(['0','15','30','45'])">Quarters</a>
        <a class="preset-link" (click)="setMinutes(['0','30'])">Halves</a>
        <a class="preset-link" (click)="setMinutes(['0'])">Top of hour</a>
        <a class="preset-link clear-link" (click)="setMinutes([])">Clear</a>
      </span>
    </div>

    <button type="button" class="hours-summary" (click)="minutesExpanded = !minutesExpanded">
      <span class="hours-summary-text">{{ minutesSummary() }}</span>
      <mat-icon class="hours-summary-chevron"
                [class.expanded]="minutesExpanded">expand_more</mat-icon>
    </button>

    <!-- Always mounted (toggled via [hidden]) — see hours-grid note above.
         *ngIf re-creates the listbox on every expand, which races Material's
         [multiple] init and silently downgrades to single-select after a
         collapse cycle. That was the [5,25] bug the user hit: clicking 25
         after collapsing was overwriting [5] instead of appending. -->
    <mat-chip-listbox [multiple]="true"
                      [formControl]="minutesControl"
                      class="minutes-grid compact-grid"
                      [hidden]="!minutesExpanded">
      <mat-chip-option *ngFor="let m of minutes" [value]="m">{{ m }}</mat-chip-option>
    </mat-chip-listbox>
  </div>

  <ng-container *ngIf="isHourlyExecuteEvery()">
    <mat-form-field appearance="outline" class="every-min-field">
      <mat-label>Execute every</mat-label>
      <span matTextPrefix>Every &nbsp;</span>
      <span matTextSuffix>&nbsp;Minutes</span>
      <!-- No upper bound — 13 = every 13 min rolling, 120 = every 2 hours, 180
           = every 3 hours, etc. The pattern crosses hour boundaries; only
           fires landing in selected Hours of Day are emitted. -->
      <input matInput type="number" [formControl]="executeEveryMinutesControl" placeholder="Every X minutes" min="1">
      <mat-error *ngIf="hourlyOptionsExceuteEveryHasError()">{{hourlyOptionsExceuteEveryErrorMessage()}}</mat-error>
    </mat-form-field>

    <!-- Rolling anchor — the every-N pattern aligns to this time-of-day. With
         everyMinutes=120 + anchor=00:30 the schedule fires at 00:30, 02:30, …
         For odd-hour offsets (01:30, 03:30, …) just pick anchor=01:30. Same
         clock-face picker used by Daily's executionTime. -->
    <mat-form-field appearance="outline" class="every-min-field" (click)="anchorTimePicker.open()">
      <mat-label>Anchor time</mat-label>
      <input matInput
             [formControl]="anchorTimeControl"
             [ngxMatTimepicker]="anchorTimePicker"
             placeholder="00:30 (rolling anchor)" />
      <mat-icon matSuffix>schedule</mat-icon>
      <ngx-mat-timepicker #anchorTimePicker [enableKeyboardInput]="true"></ngx-mat-timepicker>
      <mat-hint align="start">Pattern aligns to this HH:mm. Blank = midnight.</mat-hint>
      <mat-hint align="end" *ngIf="anchorTimeControl.value">
        <a class="date-clear-link"
           (click)="$event.stopPropagation(); anchorTimeControl.setValue('')">Clear</a>
      </mat-hint>
    </mat-form-field>
  </ng-container>
</div>
